/**
 * Marketplace API client — thin wrapper over the shared api fetch
 * helper. Mirrors the contract in backend/src/handlers/marketplace.js:
 *
 *   listWorkflows({ category, author, sort, cursor, limit })
 *     → { items, nextCursor }
 *
 *   listTrending(limit)            → { items }
 *   listCategories()               → { categories: [{ name, count }] }
 *   getWorkflow(slug)              → { slug, name, description, ... }
 *   deployWorkflow(slug, projectId)
 *                                  → { alreadyInstalled, workflowSlug, projectId }
 *   rateWorkflow(slug, { rating, review })
 *                                  → { rating, review, ratingCount, ratingAvg }
 *   listRatings(slug, { cursor })  → { items, nextCursor }
 *
 * The marketplace API is BasePathMapped onto api-${stage}.zibby.app/marketplace/*,
 * so we hit `${API_BASE_URL}/marketplace/workflows...` and the shared
 * api client tacks on the JWT bearer header automatically.
 */

import api from './api';

const BASE = '/marketplace/workflows';

function qs(params) {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v === undefined || v === null || v === '') continue;
    u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

export const Marketplace = {
  listWorkflows({ category, author, sort, cursor, limit } = {}) {
    return api.get(`${BASE}${qs({ category, author, sort, cursor, limit })}`);
  },

  listTrending(limit = 10) {
    return api.get(`${BASE}/trending${qs({ limit })}`);
  },

  listCategories() {
    return api.get(`${BASE}/categories`);
  },

  getWorkflow(slug) {
    if (!slug) return Promise.reject(new Error('slug is required'));
    return api.get(`${BASE}/${encodeURIComponent(slug)}`);
  },

  /**
   * @param {string} slug
   * @param {string} projectId
   * @param {Object} [opts]
   * @param {number} [opts.warm]  0..5 warm Fargate tasks. Omit / undefined
   *   = inherit cold-start default. Server validates the range.
   * @param {string} [opts.workflowName]  Override workflowType (the URL
   *   slug under which the workflow lands in this project). Server
   *   validates shape + uniqueness; 409 on collision with a
   *   server-suggested alternative.
   * @param {boolean} [opts.force]  App-Store-style "Update" — re-copies
   *   the latest bundle into an existing install. Server-side preserves
   *   env vars, triggers, displayName, warm config. 404 if no prior
   *   install exists for (projectId, workflowName).
   */
  deployWorkflow(slug, projectId, opts = {}) {
    if (!slug || !projectId) return Promise.reject(new Error('slug and projectId are required'));
    const body = { projectId };
    if (typeof opts.warm === 'number') body.warm = opts.warm;
    if (typeof opts.workflowName === 'string' && opts.workflowName.trim()) {
      body.workflowName = opts.workflowName.trim();
    }
    if (opts.force === true) body.force = true;
    return api.post(`${BASE}/${encodeURIComponent(slug)}/deploy`, body);
  },

  /**
   * GET /marketplace/workflows/{slug}/integration-status?projectId=<id>
   *
   * Returns { required, connected, missing, integrations } so the install
   * modal can render a ✓/✗ checklist and a "connect after deploy" CTA per
   * missing integration. Same shape as the per-workflow status endpoint.
   */
  getIntegrationStatus(slug, projectId) {
    if (!slug || !projectId) return Promise.reject(new Error('slug and projectId are required'));
    return api.get(`${BASE}/${encodeURIComponent(slug)}/integration-status${qs({ projectId })}`);
  },

  rateWorkflow(slug, { rating, review = '' } = {}) {
    if (!slug) return Promise.reject(new Error('slug is required'));
    return api.post(`${BASE}/${encodeURIComponent(slug)}/ratings`, { rating, review });
  },

  listRatings(slug, { cursor, limit } = {}) {
    if (!slug) return Promise.reject(new Error('slug is required'));
    return api.get(`${BASE}/${encodeURIComponent(slug)}/ratings${qs({ cursor, limit })}`);
  },
};

export default Marketplace;
